import { useId } from "react";
import { MARCADORES_POR_FOLHA, TAMANHO_MAXIMO_DO_ESTILO } from "@foto/shared";
import { trpc, type SaidaRota } from "../../lib/trpc.js";
import { Area, Botao, Campo, Falha, Girando, Selecao, cx } from "../../components/ui.js";
import {
  direcaoValida,
  escolherEstilo,
  marcadorQueUsa,
  type EstadoDaArte,
  type ModoDeArte,
} from "./estilos.js";

type Modelo = SaidaRota["modelosDeMarcador"]["list"][number];

/**
 * Os jeitos de dizer como a arte sai. O texto da frase é o que o operador lê ao escolher, então
 * diz o que acontece, não como: "Automático" não fala de diretor nem de catálogo.
 */
const MODOS: { valor: ModoDeArte; rotulo: string; sobre: string }[] = [
  {
    valor: "automatico",
    rotulo: "Automático",
    sobre: "A IA escolhe três estilos que combinam com o texto e cria o cenário do tema.",
  },
  {
    valor: "escolher",
    rotulo: "Escolher estilos",
    sobre: "Você escolhe o estilo de cada marcador, e a IA cria o cenário do tema.",
  },
  {
    valor: "modelo",
    rotulo: "Modelo pronto",
    sobre:
      "Usa as três direções de arte de um modelo salvo e sugere os textos dele. Você pode ajustá-las.",
  },
  {
    valor: "escrever",
    rotulo: "Escrever eu mesmo",
    sobre: "Você escreve a direção de arte de cada marcador.",
  },
];

/** O toque que abre um "Ver…": a linha inteira, com pelo menos 44 px de altura. */
const RESUMO = "-mx-3 cursor-pointer px-3 py-3 text-[0.875rem] text-texto-suave";

/**
 * A seção "Estilos de arte" do formulário de geração.
 *
 * Controlada: o estado mora na página, que precisa dele para montar o pedido e para saber se já
 * dá para gerar. Os quatro modos são exclusivos — escolher um não apaga o que se digitou nos
 * outros, mas só o modo escolhido vai no pedido (ver `pedidoDeArte`).
 *
 * O **modelo pré-criado é um dos modos**, e não um seletor à parte: ele já define as três
 * direções, então conviver com "Escolher estilos" ou "Escrever" na mesma tela deixaria a dúvida
 * de qual vale. Como modo, a pergunta nem existe.
 */
export function EstilosDeArte({
  arte,
  aoMudar,
  modelos,
  aoEscolherModelo,
  falta,
}: {
  arte: EstadoDaArte;
  aoMudar: (nova: EstadoDaArte) => void;
  modelos: readonly Modelo[];
  /** A página também sugere os textos do modelo, por isso a escolha dele passa por ela. */
  aoEscolherModelo: (valor: string) => void;
  /** O que falta para poder gerar, quando for da arte. */
  falta: string | null;
}) {
  const nomeDoGrupo = useId();
  const idDaFrase = useId();

  // Sem nenhum modelo, "Modelo pronto" levaria a um seletor vazio: some. Se já estiver escolhido
  // (a lista recarregou vazia), fica, para não sumir o botão debaixo do dedo.
  const modos = MODOS.filter(
    (m) => m.valor !== "modelo" || modelos.length > 0 || arte.modo === "modelo",
  );
  const frase = MODOS.find((m) => m.valor === arte.modo)?.sobre;

  return (
    <fieldset className="flex min-w-0 flex-col gap-3" aria-describedby={idDaFrase}>
      <legend className="mb-2 text-[0.8125rem] font-medium text-texto-suave">
        Estilos de arte
      </legend>

      <div className="grid grid-cols-2 gap-2">
        {modos.map((m) => (
          <label key={m.valor} className="relative block">
            {/* O rádio nativo fica escondido, mas segue sendo ele que o leitor de tela e o
                teclado enxergam (setas trocam de modo); o `span` é só a aparência. */}
            <input
              type="radio"
              name={nomeDoGrupo}
              value={m.valor}
              checked={arte.modo === m.valor}
              onChange={() => aoMudar({ ...arte, modo: m.valor })}
              className="peer sr-only"
            />
            <span
              className={cx(
                "flex min-h-11 items-center justify-center rounded-peca border px-2 py-2",
                "text-center text-[0.875rem] leading-tight transition-colors",
                "border-borda text-texto-suave hover:border-borda-forte hover:text-texto",
                "peer-checked:border-brasa peer-checked:bg-brasa-fraca peer-checked:text-brasa",
                "peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brasa",
              )}
            >
              {m.rotulo}
            </span>
          </label>
        ))}
      </div>

      <p
        id={idDaFrase}
        aria-live="polite"
        className="text-[0.8125rem] leading-relaxed text-texto-suave"
      >
        {frase}
      </p>

      {arte.modo === "escolher" && (
        <EscolhaDeEstilos
          escolha={arte.escolha}
          aoEscolher={(marcador, id) =>
            aoMudar({ ...arte, escolha: escolherEstilo(arte.escolha, marcador, id) })
          }
        />
      )}

      {arte.modo === "modelo" && (
        <ModeloPronto
          arte={arte}
          aoMudar={aoMudar}
          modelos={modelos}
          aoEscolherModelo={aoEscolherModelo}
        />
      )}

      {arte.modo === "escrever" && (
        <div className="flex flex-col gap-3">
          {arte.escritas.map((d, i) => (
            <Campo
              key={i}
              rotulo={`Marcador ${i + 1}`}
              dica={
                i === 0
                  ? "Ambientação, cores, tipografia e enfeites. Ex.: amanhecer dourado, céu azul-claro, título em serifa azul-marinho."
                  : undefined
              }
            >
              <Area
                value={d}
                onChange={(e) => {
                  const novas = [...arte.escritas];
                  novas[i] = e.target.value;
                  aoMudar({ ...arte, escritas: novas });
                }}
                maxLength={TAMANHO_MAXIMO_DO_ESTILO}
                className="min-h-[5rem]"
              />
            </Campo>
          ))}
        </div>
      )}

      {falta && <p className="text-[0.8125rem] text-texto-fraco">{falta}</p>}
    </fieldset>
  );
}

/**
 * Um seletor por marcador, cada opção pelo nome do estilo. O que um estilo já usa em outro
 * marcador fica desabilitado e diz onde está: repetir não vale, e o motivo aparece na própria lista.
 * A descrição do estilo escolhido vem logo abaixo; a dos demais, na lista "Ver os estilos".
 */
function EscolhaDeEstilos({
  escolha,
  aoEscolher,
}: {
  escolha: readonly string[];
  aoEscolher: (marcador: number, id: string) => void;
}) {
  // Só busca quando o modo é aberto; o catálogo só muda com um deploy, então não se busca de novo.
  const consulta = trpc.marcadores.estilosDeArte.useQuery(undefined, { staleTime: Infinity });
  if (consulta.isLoading) return <Girando className="mx-auto my-4 size-5 text-texto-fraco" />;
  if (consulta.isError) {
    return (
      <Falha
        mensagem={`Não foi possível carregar os estilos. ${consulta.error.message}`}
        tentarNovamente={() => void consulta.refetch()}
      />
    );
  }
  const lista = consulta.data?.estilos ?? [];

  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: MARCADORES_POR_FOLHA }, (_, marcador) => {
        const escolhido = lista.find((e) => e.id === escolha[marcador]);
        return (
          <Campo key={marcador} rotulo={`Marcador ${marcador + 1}`} dica={escolhido?.descricao}>
            <Selecao
              value={escolha[marcador] ?? ""}
              onChange={(e) => aoEscolher(marcador, e.target.value)}
            >
              <option value="">Escolha um estilo</option>
              {lista.map((e) => {
                const noutro = marcadorQueUsa(escolha, e.id, marcador);
                return (
                  <option key={e.id} value={e.id} disabled={noutro !== -1}>
                    {e.nome}
                    {noutro !== -1 ? ` — já no marcador ${noutro + 1}` : ""}
                  </option>
                );
              })}
            </Selecao>
          </Campo>
        );
      })}

      <details className="rounded-peca border border-borda bg-superficie px-3">
        <summary className={RESUMO}>Ver como é cada estilo</summary>
        <dl className="flex flex-col gap-2.5 pb-3">
          {lista.map((e) => (
            <div key={e.id}>
              <dt className="text-[0.875rem] font-medium text-texto">{e.nome}</dt>
              <dd className="text-[0.8125rem] leading-relaxed text-texto-suave">{e.descricao}</dd>
            </div>
          ))}
        </dl>
      </details>
    </div>
  );
}

/** O seletor de modelo e, escolhido um, as três direções dele para ver ou ajustar. */
function ModeloPronto({
  arte,
  aoMudar,
  modelos,
  aoEscolherModelo,
}: {
  arte: EstadoDaArte;
  aoMudar: (nova: EstadoDaArte) => void;
  modelos: readonly Modelo[];
  aoEscolherModelo: (valor: string) => void;
}) {
  const modelo = modelos.find((m) => String(m.id) === arte.modeloId);
  const direcoes = arte.ajustadas ?? modelo?.estilos ?? null;

  return (
    <div className="flex flex-col gap-3">
      <Campo
        rotulo="Modelo"
        dica={
          modelo
            ? (modelo.descricao ?? "As três direções de arte deste modelo.")
            : "Os modelos ficam em “Modelos de marcador”."
        }
      >
        <Selecao value={arte.modeloId} onChange={(e) => aoEscolherModelo(e.target.value)}>
          <option value="">Escolha um modelo</option>
          {modelos.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nome}
              {m.doSistema ? " (do sistema)" : ""}
            </option>
          ))}
        </Selecao>
      </Campo>

      {direcoes && (
        <details className="rounded-peca border border-borda bg-superficie px-3">
          <summary className={RESUMO}>Ver ou ajustar as três direções</summary>
          <div className="flex flex-col gap-3 pb-3">
            {direcoes.map((d, i) => (
              <Campo
                key={i}
                rotulo={`Marcador ${i + 1}`}
                dica={
                  arte.ajustadas && !direcaoValida(d) ? "Escreva pelo menos 10 letras." : undefined
                }
              >
                <Area
                  value={d}
                  onChange={(e) => {
                    const novas = [...direcoes];
                    novas[i] = e.target.value;
                    aoMudar({ ...arte, ajustadas: novas });
                  }}
                  maxLength={TAMANHO_MAXIMO_DO_ESTILO}
                  className="min-h-[5rem]"
                />
              </Campo>
            ))}
            {arte.ajustadas && (
              <Botao
                tom="fantasma"
                formato="nu"
                className="min-h-11"
                onClick={() => aoMudar({ ...arte, ajustadas: null })}
              >
                Voltar ao que o modelo traz
              </Botao>
            )}
          </div>
        </details>
      )}
    </div>
  );
}
